import { useRecordContext } from "ra-core";
import {
  Building2,
  IndianRupee,
  MapPin,
  PhoneCall,
  Tag,
  Users,
} from "lucide-react";
import type { Contact } from "../types";

const formatINR = (val?: number | string | null): string => {
  if (val == null || val === "") return "";
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(num)) return "";
  if (num >= 10000000) {
    return `₹${(num / 10000000).toFixed(2)} Cr`;
  }
  if (num >= 100000) {
    return `₹${(num / 100000).toFixed(2)} L`;
  }
  return `₹${num.toLocaleString("en-IN")}`;
};

export const ContactRealEstateInfo = () => {
  const record = useRecordContext<Contact>();

  if (!record) return null;

  const hasReData =
    record.lead_source ||
    record.budget_min ||
    record.budget_max ||
    record.preferred_type ||
    record.preferred_location ||
    record.phone_secondary ||
    record.channel_partner_id;

  if (!hasReData) {
    return (
      <div className="text-xs text-muted-foreground italic py-1">
        No real estate requirement preferences set.
      </div>
    );
  }

  const budgetMinStr = formatINR(record.budget_min);
  const budgetMaxStr = formatINR(record.budget_max);
  const budgetDisplay =
    budgetMinStr && budgetMaxStr
      ? `${budgetMinStr} - ${budgetMaxStr}`
      : budgetMinStr || budgetMaxStr || "Not specified";

  return (
    <div className="flex flex-col gap-2.5 text-sm py-1">
      {record.lead_source && (
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">Source:</span>
          <span className="font-medium">{record.lead_source}</span>
        </div>
      )}

      {record.phone_secondary && (
        <div className="flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">Alt Phone:</span>
          <span className="font-medium">{record.phone_secondary}</span>
        </div>
      )}

      {(record.budget_min || record.budget_max) && (
        <div className="flex items-center gap-2">
          <IndianRupee className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">Budget:</span>
          <span className="font-medium text-emerald-600 dark:text-emerald-400">
            {budgetDisplay}
          </span>
        </div>
      )}

      {record.preferred_type && (
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">Type:</span>
          <span className="font-medium">{record.preferred_type}</span>
        </div>
      )}

      {record.preferred_location && (
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">Location:</span>
          <span className="font-medium">{record.preferred_location}</span>
        </div>
      )}

      {record.channel_partner_id && (
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">CP ID:</span>
          <span className="font-medium">{record.channel_partner_id}</span>
        </div>
      )}
    </div>
  );
};
